import { useCallback, useEffect, useMemo, useState } from "react";

import "./benchmark-creation-goal-display.css";

import DateField from "../DateField";
import SearchableStringListDropdown from "../Filters/SearchableStringListDropdown";

import { BenchmarkGoal } from "../../types/Quality/Benchmark";
import { ActivityTrade } from "../../types/Quality/ActivityTrade";

const INACTIVE_SUFFIX = " (inactive)";
const NO_TRADES_SUFFIX = " (no trades)";

const formatOptionLabel = (value?: string, isActive: boolean = true) => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    return isActive ? trimmed : `${trimmed}${INACTIVE_SUFFIX}`;
};

const normalizeSelectionValue = (value?: string) => {
    if (!value) return "";
    const trimmed = value.trim();
    if (trimmed.endsWith(INACTIVE_SUFFIX)) {
        return trimmed.slice(0, -INACTIVE_SUFFIX.length);
    }
    if (trimmed.endsWith(NO_TRADES_SUFFIX)) {
        return trimmed.slice(0, -NO_TRADES_SUFFIX.length);
    }
    return trimmed;
};

type BenchmarkCreationGoalDisplayProps = {
    goal: BenchmarkGoal;
    availableActivityTrades?: ReadonlyArray<ActivityTrade>;
    canEdit?: boolean;
    onEdit: (goal: BenchmarkGoal) => void;
    isEditing?: boolean;
    isDeleting?: boolean;
};

const BenchmarkCreationGoalDisplay = ({
    goal,
    availableActivityTrades = [],
    canEdit = false,
    onEdit,
    isEditing = false,
    isDeleting = false,
}: BenchmarkCreationGoalDisplayProps) => {
    const [editedGoal, setEditedGoal] = useState<BenchmarkGoal>(goal);

    useEffect(() => {
        setEditedGoal(goal);
    }, [goal]);

    useEffect(() => {
        onEdit(editedGoal);
    }, [editedGoal, onEdit]);

    // sets the incomming editedGoal trade default value
    const tradeDefaultValue = useMemo(() => {
        const trimmed = editedGoal.trade?.name?.trim();
        if (!trimmed) return undefined;

        const match = availableActivityTrades.find(
            (activityTrade) => activityTrade.tradeName === trimmed
        );
        return formatOptionLabel(trimmed, match ? match.tradeIsActive : true);
    }, [editedGoal.trade, availableActivityTrades]);

    // build the availble trade options
    const { tradeOptions, tradeCandidateNames } = useMemo(() => {
        const selectedActivity = editedGoal.activity?.name?.trim();
        const relevantTrades = selectedActivity
            ? availableActivityTrades.filter(
                (activityTrade) => activityTrade.activityName?.trim() === selectedActivity
            )
            : availableActivityTrades;

        const uniqueTrades = new Map<string, boolean>();

        relevantTrades.forEach((activityTrade) => {
            const tradeName = activityTrade.tradeName?.trim();
            if (!tradeName) {
                return;
            }

            const current = uniqueTrades.get(tradeName);
            if (current === undefined) {
                uniqueTrades.set(tradeName, activityTrade.tradeIsActive);
            } else if (!current && activityTrade.tradeIsActive) {
                uniqueTrades.set(tradeName, true);
            }
        });

        const candidateEntries = Array.from(uniqueTrades.entries());

        const options = candidateEntries
            .map(([name, isActive]) => formatOptionLabel(name, isActive))
            .filter((value): value is string => Boolean(value));

        if (tradeDefaultValue && !options.includes(tradeDefaultValue)) {
            options.push(tradeDefaultValue);
        }

        const sortedOptions = options.sort((a, b) => a.localeCompare(b));

        return {
            tradeOptions: sortedOptions,
            tradeCandidateNames: candidateEntries.map(([name]) => name),
        };
    }, [availableActivityTrades, tradeDefaultValue, editedGoal.activity]);

    // build the disabled trade options (these will be inactive trades selection)
    const disabledTrades = useMemo(() => {
        const selectedActivity = editedGoal.activity?.name?.trim();
        const relevantTrades = selectedActivity
            ? availableActivityTrades.filter(
                (activityTrade) => activityTrade.activityName?.trim() === selectedActivity
            )
            : availableActivityTrades;

        const inactiveTrades = new Set<string>();

        relevantTrades.forEach((activityTrade) => {
            const tradeName = activityTrade.tradeName?.trim();
            if (!tradeName) {
                return;
            }

            if (!activityTrade.tradeIsActive) {
                inactiveTrades.add(tradeName);
            } else if (inactiveTrades.has(tradeName)) {
                // If we previously marked it inactive but encounter an active entry, remove it
                inactiveTrades.delete(tradeName);
            }
        });

        return Array.from(inactiveTrades.values())
            .map((name) => formatOptionLabel(name, false))
            .filter((value): value is string => Boolean(value));
    }, [availableActivityTrades, editedGoal.activity]);

    // sets the incomming editedGoal activity default value
    const activityDefaultValue = useMemo(() => {
        const trimmed = editedGoal.activity?.name.trim();
        if (!trimmed) return undefined;

        const match = availableActivityTrades.find(
            (activityTrade) => activityTrade.activityName === trimmed
        );

        return match && match.tradeName === ''
            ? `${trimmed}${NO_TRADES_SUFFIX}`
            : formatOptionLabel(trimmed, match ? match.activityIsActive : true);
    }, [editedGoal.activity, availableActivityTrades]);

    // build the availble activity options
    const { activityOptions, activityCandidateNames } = useMemo(() => {
        const selectedTrade = editedGoal.trade?.name?.trim();
        const relevantActivities = selectedTrade
            ? availableActivityTrades.filter(
                (activityTrade) => activityTrade.tradeName?.trim() === selectedTrade
            )
            : availableActivityTrades;

        const uniqueActivities = new Map<string, { isActive: boolean; hasTrades: boolean }>();

        relevantActivities.forEach((activityTrade) => {
            const activityName = activityTrade.activityName?.trim();
            if (!activityName) {
                return;
            }

            const hasTrades = Boolean(activityTrade.tradeName?.trim());
            const existing = uniqueActivities.get(activityName);

            if (!existing) {
                uniqueActivities.set(activityName, {
                    isActive: activityTrade.activityIsActive,
                    hasTrades,
                });
            } else {
                existing.isActive = existing.isActive || activityTrade.activityIsActive;
                existing.hasTrades = existing.hasTrades || hasTrades;
            }
        });

        const candidateEntries = Array.from(uniqueActivities.entries());

        const options = candidateEntries.map(([name, { isActive, hasTrades }]) => {
            if (!hasTrades) {
                return `${name}${NO_TRADES_SUFFIX}`;
            }
            return formatOptionLabel(name, isActive);
        }).filter((value): value is string => Boolean(value));

        if (activityDefaultValue && !options.includes(activityDefaultValue)) {
            options.push(activityDefaultValue);
        }

        const sortedOptions = options.sort((a, b) => a.localeCompare(b));

        return {
            activityOptions: sortedOptions,
            activityCandidateNames: candidateEntries.map(([name]) => name),
        };
    }, [availableActivityTrades, activityDefaultValue, editedGoal.trade]);

    // build the disabled activity options (these will be inactive activities or those without trades)
    const disabledActivities = useMemo(() => {
        const selectedTrade = editedGoal.trade?.name?.trim();
        const relevantActivities = selectedTrade
            ? availableActivityTrades.filter(
                (activityTrade) => activityTrade.tradeName?.trim() === selectedTrade
            )
            : availableActivityTrades;

        const disabled = new Set<string>();
        const pendingActive = new Set<string>();

        relevantActivities.forEach((benchmark) => {
            const activityName = benchmark.activityName?.trim();
            if (!activityName) {
                return;
            }

            const hasTrades = Boolean(benchmark.tradeName?.trim());
            const isActive = benchmark.activityIsActive && hasTrades;

            if (!isActive) {
                if (!hasTrades) {
                    disabled.add(`${activityName}${NO_TRADES_SUFFIX}`);
                } else if (!benchmark.activityIsActive) {
                    disabled.add(formatOptionLabel(activityName, false) ?? activityName);
                }
                return;
            }

            // Active entry with trades → ensure the activity isn't flagged as disabled
            pendingActive.add(activityName);
        });

        pendingActive.forEach((name) => {
            disabled.delete(`${name}${NO_TRADES_SUFFIX}`);
            disabled.delete(formatOptionLabel(name, false) ?? name);
        });

        return Array.from(disabled.values())
            .filter((value): value is string => Boolean(value));
    }, [availableActivityTrades, editedGoal.trade]);

    const handleTradeSelection = useCallback((trade?: string) => {
        const normalizedTrade = normalizeSelectionValue(trade);

        setEditedGoal((currentGoal) => {
            const selectedActivityName = currentGoal.activity?.name?.trim();
            const matchingTrade = normalizedTrade
                ? availableActivityTrades.find((activityTrade) => {
                    if (activityTrade.tradeName?.trim() !== normalizedTrade) {
                        return false;
                    }

                    if (selectedActivityName) {
                        return activityTrade.activityName?.trim() === selectedActivityName;
                    }

                    return true;
                })
                : undefined;

            return {
                ...currentGoal,
                trade: {
                    ...currentGoal.trade,
                    id: normalizedTrade ? matchingTrade?.tradeID ?? 0 : 0,
                    name: normalizedTrade,
                    isActive: normalizedTrade ? (matchingTrade?.tradeIsActive ?? true) : true,
                },
            };
        });
    }, [availableActivityTrades]);

    const handleActivitySelection = useCallback((activity?: string) => {
        const normalizedActivity = normalizeSelectionValue(activity);

        setEditedGoal((currentGoal) => {
            const selectedTradeName = currentGoal.trade?.name?.trim();
            const matchingActivity = normalizedActivity
                ? availableActivityTrades.find((activityTrade) => {
                    if (activityTrade.activityName?.trim() !== normalizedActivity) {
                        return false;
                    }

                    if (selectedTradeName) {
                        return activityTrade.tradeName?.trim() === selectedTradeName;
                    }

                    return true;
                })
                : undefined;

            return {
                ...currentGoal,
                activity: {
                    ...currentGoal.activity,
                    id: normalizedActivity ? matchingActivity?.activityID ?? 0 : 0,
                    name: normalizedActivity,
                    nameNormalized: normalizedActivity ? normalizedActivity.toLowerCase() : '',
                    isActive: normalizedActivity ? (matchingActivity?.activityIsActive ?? true) : true,
                },
            };
        });
    }, [availableActivityTrades]);

    useEffect(() => {
        if (!canEdit) return;
        const selectedActivityName = editedGoal.activity?.name?.trim();
        if (!selectedActivityName) return;
        if (tradeCandidateNames.length !== 1) return;

        const soleTradeName = tradeCandidateNames[0]?.trim();
        if (!soleTradeName) return;

        const currentTradeName = editedGoal.trade?.name?.trim();
        const currentTradeId = editedGoal.trade?.id ?? 0;
        if (soleTradeName === currentTradeName && currentTradeId !== 0) {
            return;
        }

        handleTradeSelection(soleTradeName);
    }, [
        canEdit,
        tradeCandidateNames,
        editedGoal.activity,
        editedGoal.trade,
        handleTradeSelection,
    ]);

    useEffect(() => {
        if (!canEdit) return;
        const selectedTradeName = editedGoal.trade?.name?.trim();
        if (!selectedTradeName) return;
        if (activityCandidateNames.length !== 1) return;

        const soleActivityName = activityCandidateNames[0]?.trim();
        if (!soleActivityName) return;

        const currentActivityName = editedGoal.activity?.name?.trim();
        const currentActivityId = editedGoal.activity?.id ?? 0;
        if (soleActivityName === currentActivityName && currentActivityId !== 0) {
            return;
        }

        handleActivitySelection(soleActivityName);
    }, [
        canEdit,
        activityCandidateNames,
        editedGoal.activity,
        editedGoal.trade,
        handleActivitySelection,
    ]);

    return (
        <div className="benchmark-goal-display border rounded-2 shadow-sm">
            <div className="benchmark-goal-row">
                <div className="benchmark-goal-label">Trade</div>
                <div className="benchmark-goal-value">
                    {canEdit ? (
                        <SearchableStringListDropdown
                            options={tradeOptions}
                            defaultValue={tradeDefaultValue}
                            clearable={true}
                            placeholder="Select Trade"
                            disabledValues={disabledTrades}
                            disabled={isEditing || isDeleting}
                            onChange={handleTradeSelection}
                        />
                    ) : (
                        <span>{editedGoal.trade.name}</span>
                    )}
                </div>
            </div>

            <div className="benchmark-goal-row">
                <div className="benchmark-goal-label">Activity</div>
                <div className="benchmark-goal-value">
                    {canEdit ? (
                        <SearchableStringListDropdown
                            options={activityOptions}
                            defaultValue={activityDefaultValue}
                            clearable={true}
                            placeholder="Select Activity"
                            disabledValues={disabledActivities}
                            disabled={ isEditing || isDeleting }
                            onChange={handleActivitySelection}
                        />
                    ) : (
                        <span>{editedGoal.activity.name}</span>
                    )}
                </div>
            </div>
            <div className="benchmark-goal-row">
                <div className="benchmark-goal-label">Target Completion</div>
                <div className="benchmark-goal-value">
                    {canEdit ? (
                        <DateField
                            dataName="targetCompletion"
                            title=""
                            value={editedGoal.targetCompletionDate ?? ""}
                            readonly={isDeleting}
                            onchange={(date: string) =>
                                setEditedGoal({
                                    ...editedGoal,
                                    targetCompletionDate: date ?? "",
                                })
                            }
                            className="benchmark-goal-date-field"
                            inputClassName="benchmark-goal-date-input"
                        />
                    ) : (
                        <span>{editedGoal.targetCompletionDate}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BenchmarkCreationGoalDisplay;
